import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSpinner } from "primereact/progressspinner";

import PageHeader from "../../components/PageHeader/PageHeader";
import KpiCard from "../../components/KpiCard/KpiCard";
import Button from "../../components/Button/Button";
import { Tag } from "../../components/Tag/Tag";

import { useToast } from "../../context/ToastContext";

import ProductionApi, { GrossVolumeResponse, GrossVolumePoint } from "../../services/ProductionApi";
import useLiveRefresh from "../../hooks/useLiveRefresh";
import { useShiftList } from "../../hooks/useWorkSchedule";
import ShiftFilter from "../../components/ShiftFilter/ShiftFilter";
import PeriodFilter from "../../components/PeriodFilter/PeriodFilter";
import { useActivityTracking } from "../../hooks/useActivityTracking";
import { ActivityEventType } from "../../utilities/activityLog";
import {
  describeSelection,
  DEFAULT_PERIOD_SELECTION,
  PeriodSelection,
  selectionDates,
  selectionReachesToday,
  selectionYmd,
} from "../../utilities/reportRange";
import OeeGauge from "./OeeGauge";
import styles from "./ReportOeePerformance.module.css";

/** Limite de "bom" (%) — alvo fixo de exibição no front. */
const THRESHOLD = 85;

const parseYmd = (s: string): Date => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const ReportOeePerformance: React.FC = () => {
  const { t, i18n } = useTranslation("oeePerformance");
  const { displayToast } = useToast();
  // Log de atividade: emitido SÓ nos handlers abaixo. Instrumentar dentro de load()
  // geraria evento falso a cada push do SignalR (useLiveRefresh).
  const track = useActivityTracking();

  const locale = i18n.language?.toLowerCase().startsWith("en") ? "en-US" : "pt-BR";

  const [data, setData] = useState<GrossVolumeResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // load pendente no mount — evita flash do estado vazio
  const [lastUpdated, setLastUpdated] = useState<string>("");

  const [activePeriod, setActivePeriod] = useState<PeriodSelection>(DEFAULT_PERIOD_SELECTION);
  // `null` = "Personalizado" escolhido sem as duas datas ainda → "Aplicar" desabilitado.
  const [pendingPeriod, setPendingPeriod] = useState<PeriodSelection | null>(
    DEFAULT_PERIOD_SELECTION,
  );

  const shifts = useShiftList();
  const [shiftId, setShiftId] = useState<string | null>(null);

  const formatNumber = useCallback(
    (value: number, decimals = 0): string =>
      Number(value).toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }),
    [locale],
  );

  const formatDateObj = useCallback(
    (d: Date): string => d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }),
    [locale],
  );

  /**
   * Janela {from,to} da seleção. Preset de 30 dias → `null`, para o backend
   * aplicar a janela rolante dele (é o padrão da tela); presets curtos e
   * intervalo customizado → intervalo explícito.
   */
  const resolveRange = useCallback(
    (sel: PeriodSelection): { from: string; to: string } | null =>
      sel.kind === "preset" && sel.preset === "30" ? null : selectionYmd(sel),
    [],
  );

  // OEE de produção reusa o endpoint do volume bruto (produzido × meta) — mesma fonte de dados.
  const load = useCallback(
    async (sel: PeriodSelection, sid: string | null): Promise<void> => {
      setLoading(true);
      try {
        const range = resolveRange(sel);
        const params = { ...(range ?? {}), ...(sid ? { shiftId: sid } : {}) };
        const resp = await ProductionApi.GetGrossVolume(Object.keys(params).length ? params : undefined);
        setData((resp?.data as GrossVolumeResponse) ?? null);
        setActivePeriod(sel);
        setLastUpdated(new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }));
      } catch (err: any) {
        displayToast("error", t("loadErrorTitle"), err?.errors?.[0] ?? t("loadErrorMsg"), false, 6000);
      } finally {
        setLoading(false);
      }
    },
    [resolveRange, displayToast, t, locale],
  );

  useEffect(() => {
    void load(DEFAULT_PERIOD_SELECTION, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atualização em tempo real via SignalR (substitui o botão "Atualizar").
  // Uma janela puramente histórica não muda — não vale reconsultar a cada evento
  // da estação (mesma decisão da Linha do Tempo no modo histórico).
  useLiveRefresh(
    () => void load(activePeriod, shiftId),
    selectionReachesToday(activePeriod),
  );

  const handleApply = (): void => {
    if (!pendingPeriod) return; // intervalo customizado incompleto
    track(ActivityEventType.FilterApply, describeSelection(pendingPeriod));
    void load(pendingPeriod, shiftId);
  };
  const handleReset = (): void => {
    track(ActivityEventType.FilterReset);
    setPendingPeriod(DEFAULT_PERIOD_SELECTION);
    setShiftId(null);
    void load(DEFAULT_PERIOD_SELECTION, null);
  };
  // O turno recarrega na hora (sem "Aplicar"), então carrega com a janela ATIVA.
  const handleShiftChange = (id: string | null): void => {
    track(ActivityEventType.ShiftChange, { shiftId: id });
    setShiftId(id);
    void load(activePeriod, id);
  };

  // OEE de produção = produzidos ÷ meta de produção na janela (só realizado, sem projeção).
  const derived = useMemo(() => {
    const series: GrossVolumePoint[] = data?.series ?? [];
    const projection: GrossVolumePoint[] = data?.projection ?? [];
    const dailyTarget = data?.dailyTarget ?? 0;

    // Realizado = série real; descarta zeros futuros que a projeção sobrepõe (igual ao volume bruto).
    const projStart = projection.length ? projection[0].date : null;
    let realized = projStart ? series.filter((p) => p.date < projStart) : series;
    if (realized.length === 0) realized = series;

    const produced = realized.reduce((acc, p) => acc + p.produced, 0);
    const targetTotal = realized.reduce((acc, p) => acc + (p.target ?? dailyTarget), 0);
    const oeePct = targetTotal > 0 ? (produced / targetTotal) * 100 : 0;
    const gap = produced - targetTotal;
    const productiveDays = realized.filter((p) => p.produced > 0).length;
    const days = realized.length;

    const hasData = days > 0 && realized.some((p) => p.produced > 0);

    return {
      hasData,
      produced,
      targetTotal,
      oeePct,
      gap,
      dailyTarget,
      days,
      productiveDays,
      first: realized[0]?.date,
      last: realized[realized.length - 1]?.date,
    };
  }, [data]);

  const rangeLabel = useMemo(() => {
    if (derived.first && derived.last) {
      return `${formatDateObj(parseYmd(derived.first))} – ${formatDateObj(parseYmd(derived.last))}`;
    }
    // Sem eco do backend, rotula pela janela pedida (inclui o intervalo customizado).
    const { from, to } = selectionDates(activePeriod);
    return `${formatDateObj(from)} – ${formatDateObj(to)}`;
  }, [derived, activePeriod, formatDateObj]);

  const attainGood = derived.oeePct >= THRESHOLD;

  return (
    <div className={styles.page}>
      <PageHeader title={t("title")} description={t("subtitle")} className={styles.pageHeaderFlush}>
        <Tag label={t("scopeBadge")} severity="secondary" icon="inventory_2" />
        <Tag
          label={lastUpdated ? `${t("updatedAt")} ${lastUpdated}` : t("loadingLabel")}
          severity="successInverse"
          icon="sync"
        />
      </PageHeader>

      {/* Filtros */}
      <div className={`${styles.card} ${styles.filters}`}>
        <PeriodFilter
          value={pendingPeriod}
          onChange={setPendingPeriod}
          rangeLabel={rangeLabel}
          label={t("fPeriod")}
          presetLabels={{ "7": t("preset7"), "14": t("preset14"), "30": t("preset30") }}
        />

        <ShiftFilter value={shiftId} onChange={handleShiftChange} shifts={shifts} />

        <div className={styles.filterSpacer} />

        <div className={styles.applyWrap}>
          <Button
            label={t("apply")}
            icon="check"
            severity="secondary"
            onClick={handleApply}
            loading={loading}
            disabled={!pendingPeriod}
          />
        </div>
      </div>

      {loading && !data ? (
        <div className={`${styles.card} ${styles.loading}`}>
          <ProgressSpinner style={{ width: 50, height: 50 }} strokeWidth="6" />
        </div>
      ) : derived.hasData ? (
        <>
          {/* Velocímetro */}
          <section className={styles.card}>
            <div className={styles.sectionHeader}>
              <span className={styles.sectionTitle}>
                <span className="material-symbols-outlined">speed</span>
                {t("gaugeTitle")}
              </span>
              <div className={styles.zones}>
                <span className={styles.zoneItem}>
                  <span
                    className={styles.zoneSwatch}
                    style={{ background: "var(--color-feedback-success-500, #22c55e)" }}
                  />
                  {t("zoneGood")}
                </span>
                <span className={styles.zoneItem}>
                  <span
                    className={styles.zoneSwatch}
                    style={{ background: "var(--color-feedback-danger-500, #ef4444)" }}
                  />
                  {t("zoneBad")}
                </span>
              </div>
            </div>

            <div className={styles.gaugeWrap}>
              <OeeGauge
                valuePct={derived.oeePct}
                threshold={THRESHOLD}
                locale={locale}
                labels={{ oee: t("oeeLabel"), statusGood: t("statusGood"), statusBad: t("statusBad") }}
              />
            </div>

            <div className={styles.formulaBanner}>
              <span className="material-symbols-outlined">function</span>
              <span className={styles.formulaText}>{t("formula")}</span>
            </div>
          </section>

          {/* Cards de contexto */}
          <div className={styles.kpiRow}>
            <KpiCard
              title={t("kProduced")}
              value={formatNumber(derived.produced)}
              icon="directions_car"
              variant="primary"
              trend="neutral"
              descriptionIcon="event_available"
              description={`${formatNumber(derived.productiveDays)} ${t("productiveDays")}`}
            />
            <KpiCard
              title={t("kTarget")}
              value={formatNumber(derived.targetTotal)}
              icon="flag"
              variant="info"
              trend="neutral"
              descriptionIcon="calendar_month"
              description={`${formatNumber(derived.dailyTarget, 1)}/${t("perDay")} · ${formatNumber(derived.days)} ${t("daysShort")}`}
            />
            <KpiCard
              title={t("kAttain")}
              value={`${formatNumber(derived.oeePct, 0)}%`}
              icon="percent"
              variant={attainGood ? "success" : "danger"}
              trend={attainGood ? "up" : "down"}
              descriptionIcon={attainGood ? "arrow_upward" : "arrow_downward"}
              description={`${derived.gap >= 0 ? "+" : ""}${formatNumber(derived.gap)} ${t("vsTarget")}`}
            />
          </div>
        </>
      ) : (
        <div className={styles.card}>
          <div className={styles.empty}>
            <span className={`material-symbols-outlined ${styles.emptyIcon}`}>speed</span>
            <div>
              <div className={styles.emptyTitle}>{t("emptyTitle")}</div>
              <div className={styles.emptySub}>{t("emptySub")}</div>
            </div>
            <Button
              label={t("clearFilters")}
              icon="restart_alt"
              severity="neutral"
              variant="outlined"
              onClick={handleReset}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportOeePerformance;
